import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable17({ data }) {
  const pdfRef = useRef();

  if (!data?.length) {
    return (
      <p className="text-center text-red-600 text-lg font-semibold">
        ⚠ No data available
      </p>
    );
  }

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  const colors = [
    "#F8BBD0",
    "#80DEEA",
    "#FFAB91",
    "#B7950B",
    "#9575CD",
  ];

  const datasets = data.map((item, i) => ({
    label: item.education_label,
    data: years.map((y) => Number(item[`y${y}`])),
    borderColor: colors[i % colors.length],
    backgroundColor: "transparent",
    borderWidth: 2,
    pointRadius: 6,
    pointBackgroundColor: "white",
    pointBorderColor: colors[i % colors.length],
    tension: 0.3,
  }));

  const chartData = { labels: years, datasets };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Employment Trends by Education Level (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year", font: { size: 14, weight: "bold" } } },
      y: {
        beginAtZero: false,
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
        ticks: { callback: (v) => v.toLocaleString("en-US") },
      },
    },
  };

  // ---------- SUMMARY CALCULATIONS ----------
  const latestYear = years[years.length - 1];

  const highestCategory = [...data].sort(
    (a, b) => Number(b[`y${latestYear}`]) - Number(a[`y${latestYear}`])
  )[0];

  const lowestCategory = [...data].sort(
    (a, b) => Number(a[`y${latestYear}`]) - Number(b[`y${latestYear}`])
  )[0];

  const firstTotal = data.reduce((sum, d) => sum + Number(d.y2018), 0);
  const lastTotal = data.reduce(
    (sum, d) => sum + Number(d[`y${latestYear}`]),
    0
  );
  const growthPercent = (((lastTotal - firstTotal) / firstTotal) * 100).toFixed(
    1
  );

  // ---------- DOWNLOAD PDF ----------
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("cs_it_emp_trend_by_education.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-center font-bold text-2xl mb-6">
          Employment Trends by Education Level (2018–2024)
        </h2>

        {/* Summary Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Overall Workforce Growth</p>
            <p className="text-xl font-bold text-green-800">{growthPercent}%</p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Top Workforce Level (2024)</p>
            <p className="text-xl font-bold text-blue-800">
              {highestCategory.education_label}
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">
              Lowest Workforce Level (2024)
            </p>
            <p className="text-xl font-bold text-red-800">
              {lowestCategory.education_label}
            </p>
          </div>
        </div>

        {/* Layout Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Education Level</th>
                    {years.map((year) => (
                      <th key={year} className="p-2" style={{ textAlign: 'right' }}>
                        {year}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="p-2 font-semibold text-left">
                        {row.education_label}
                      </td>
                      {years.map((year) => (
                        <td key={year} className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {Number(row[`y${year}`]).toLocaleString("en-US")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 transition px-4 py-2 rounded-lg shadow-lg text-white flex items-center gap-2"
        >
          Download as PDF
          <img className="w-[1.5rem]" src={pdf} alt="pdf" />
        </button>
      </div>
    </>
  );
}
